import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays } from 'date-fns';
import type { CalendarView } from 'digital-workplace-calendar-module';
import type { EventStatus } from '@/types/calendarTypes';
import type { BaseRoomInfo, BaseRoomBooking } from 'digital-workplace-calendar-module';
import type { GetEventsParams } from '@/store/models/events/params';
import type { BuildingId } from '@/store/models';
import type { RoomLayout, SpaceWithAvailability } from '@/store/models/spaces/entities';
import type { EventFilters } from '@/layouts/EventFilterPanel';
import { buildSeriesOrdinalTitleMap } from '@/utils/seriesEventTitle';

export const ROOM_CALENDAR_STATUS_FILTER = 'SUBMITTED,UNDER_REVIEW,CONFIRMED';

export function isVisibleCalendarEvent(summary: { eventState?: string }): boolean {
  return summary.eventState?.toUpperCase() !== 'CANCELLED';
}

export function isVisibleRoomCalendarEvent(summary: { eventState?: string }): boolean {
  if (!summary.eventState) return true;
  return ROOM_CALENDAR_STATUS_FILTER.split(',').includes(summary.eventState.toUpperCase());
}

/** Minimal shape needed from an event summary for room-booking mapping */
export interface RoomMappingSummary {
  eventId: string;
  seriesId?: string | null;
  seriesIndex?: number | null;
  seriesTotal?: number | null;
  eventTitle?: string;
  eventStartDate: string;
  eventEndDate: string;
  spaceId?: string;
  buildingId?: string;
  requestedSpace?: { spaceId?: string };
  space?: { spaceId?: string };
  spaces?: { spaceId: string }[];
}

/** Map frontend EventStatus to API EventState for the status filter param */
export const STATUS_TO_API_STATE: Record<EventStatus, string> = {
  draft: 'DRAFT',
  submitted: 'SUBMITTED',
  under_review: 'UNDER_REVIEW',
  confirmed: 'CONFIRMED',
  declined: 'DECLINED',
  request_for_cancellation: 'SUBMITTED_FOR_CANCELLATION',
  cancelled: 'CANCELLED',
  completed: 'COMPLETED',
  did_not_occur: 'NO_OCCURRENCE',
};

const normalizeSetupCode = (code: string): string =>
  code
    .trim()
    .toLowerCase()
    .replace(/[-_\s]/g, '');

export function isSetupSelectionOverCapacity(
  selectedCodes: string[],
  layouts: { code: string; capacity: number }[],
  roomCapacity: number | undefined
): boolean {
  if (selectedCodes.length === 0 || typeof roomCapacity !== 'number') return false;

  const normalizedSelections = new Set<string>();
  for (const code of selectedCodes) normalizedSelections.add(normalizeSetupCode(code));

  let selectedCapacity = 0;
  for (const layout of layouts) {
    if (normalizedSelections.has(normalizeSetupCode(layout.code))) {
      selectedCapacity += layout.capacity;
    }
  }
  return selectedCapacity > roomCapacity;
}

export function findSpaceById(
  spaces: SpaceWithAvailability[],
  spaceId: string
): SpaceWithAvailability | undefined {
  for (const space of spaces) {
    if ((space.spaceId as string) === spaceId) return space;
  }
  return undefined;
}

export function getSetupCodesForSpace(
  spaces: { spaceId?: unknown; selectedSetupCodes?: string[] }[] | undefined,
  spaceId: string | undefined
): string[] {
  if (!spaces || !spaceId) return [];
  for (const space of spaces) {
    if ((space.spaceId as string) === spaceId) return space.selectedSetupCodes ?? [];
  }
  return [];
}

export function resolveRoomSetupOptions(
  targetSpace: SpaceWithAvailability | undefined,
  sourceSetupCodes: string[]
): RoomLayout[] {
  const supportedLayouts = targetSpace?.supportedRoomLayouts ?? [];
  if (supportedLayouts.length > 0) return supportedLayouts;
  if (targetSpace?.isBoardRoom) return [{ code: 'boardroom', capacity: 0 }];

  const options: RoomLayout[] = [];
  const seenCodes = new Set<string>();
  for (const code of sourceSetupCodes) {
    if (!seenCodes.has(code)) {
      seenCodes.add(code);
      options.push({ code, capacity: 0 });
    }
  }
  return options;
}

export function getOnlyAvailableSetupCode(
  availability: string,
  options: RoomLayout[]
): string | undefined {
  return availability === 'available' && options.length === 1 ? options[0]?.code : undefined;
}

/** Format a Date as YYYY-MM-DD */
function formatDateParam(dt: Date): string {
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

/** Compute the start/end date range strings for a given view and date.
 *
 * The window is widened by one full day on BOTH sides of the visible range. The
 * calendar grid renders events in the viewer's LOCAL timezone, but the API
 * filters against UTC-stored timestamps using these date-only boundaries. An
 * event near the local/UTC day boundary shifts into an adjacent local day once
 * converted (e.g. `2026-06-18T02:00Z` shows on June 17 for a UTC-7 viewer), so
 * without the slack it would fall outside the fetched window and silently
 * disappear. Padding ±1 day guarantees the fetched set is a superset of the
 * visible local days (max real TZ offset is ±14h < 1 day); the grid then slices
 * each event onto its correct local day. The extra fetched events are harmless —
 * they are filtered out by the per-day bucketing.
 */
export function getDateRangeForView(
  date: Date,
  view: CalendarView
): { startDate: string; endDate: string } {
  const d = new Date(date);
  let startDate: Date;
  let endDate: Date;

  if (view === 'month') {
    // Use the visible grid range (includes days from adjacent months shown on the calendar)
    const monthStart = startOfMonth(d);
    const monthEnd = endOfMonth(d);
    startDate = startOfWeek(monthStart, { weekStartsOn: 0 });
    endDate = addDays(endOfWeek(monthEnd, { weekStartsOn: 0 }), 1);
  } else if (view === 'week') {
    const dayOfWeek = d.getDay();
    startDate = new Date(d);
    startDate.setDate(d.getDate() - dayOfWeek);
    endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 7);
  } else {
    startDate = new Date(d);
    endDate = new Date(d);
    endDate.setDate(d.getDate() + 1);
  }

  // Pad ±1 day so timezone-shifted events at either boundary are still fetched.
  // The local-time grid filtering (getEventPiecesForDay / getEventsForDay) then
  // places each event on its correct day.
  startDate = addDays(startDate, -1);
  endDate = addDays(endDate, 1);

  return { startDate: formatDateParam(startDate), endDate: formatDateParam(endDate) };
}

/** Build GetEventsParams from a date range and optional event filters */
export function buildEventsQueryParams(
  dateRange: { startDate: string; endDate: string },
  eventFilters?: EventFilters
): GetEventsParams {
  const params: GetEventsParams = {
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    pageSize: 100,
  };

  if (eventFilters) {
    if (eventFilters.statuses.length > 0) {
      params.status = eventFilters.statuses.map((s) => STATUS_TO_API_STATE[s]).join(',');
    }
    if (eventFilters.buildingIds.length > 0) {
      params.buildingId = eventFilters.buildingIds[0] as BuildingId;
    }
    if (eventFilters.ownerEmails.length > 0) {
      params.owner = eventFilters.ownerEmails.join(',');
    }
  }

  return params;
}

/** Map EventSummary[] to room bookings and a booking-to-event ID map */
export function mapSummariesToRoomBookings(
  summaries: RoomMappingSummary[],
  spaceRooms: BaseRoomInfo[],
  fallbackTitle: string
): { bookings: BaseRoomBooking[]; bookingToEventId: Map<string, string> } {
  const displayTitleByEventId = buildSeriesOrdinalTitleMap(
    summaries.map((summary) => ({
      eventId: summary.eventId,
      seriesIndex: summary.seriesIndex,
      seriesTotal: summary.seriesTotal,
      eventTitle: summary.eventTitle ?? fallbackTitle,
    })),
    (event) => event.eventTitle
  );
  const bookings: BaseRoomBooking[] = [];
  const idMap = new Map<string, string>();

  for (const s of summaries) {
    const displayTitle = displayTitleByEventId.get(s.eventId)!;

    // Fan out across all assigned spaces when available
    if (s.spaces && s.spaces.length > 0) {
      for (const space of s.spaces) {
        const bookingId = `${s.eventId}_${space.spaceId}`;
        bookings.push({
          id: bookingId,
          eventTitle: displayTitle,
          roomId: space.spaceId,
          start: new Date(s.eventStartDate),
          end: new Date(s.eventEndDate),
        });
        idMap.set(bookingId, s.eventId);
      }
    } else {
      // Legacy fallback: single room from requestedSpace/spaceId
      const roomSpaceId = s.requestedSpace?.spaceId ?? s.spaceId;
      if (roomSpaceId) {
        bookings.push({
          id: s.eventId,
          eventTitle: displayTitle,
          roomId: roomSpaceId,
          start: new Date(s.eventStartDate),
          end: new Date(s.eventEndDate),
        });
        idMap.set(s.eventId, s.eventId);
      } else if (s.buildingId) {
        const buildingRooms = spaceRooms.filter(
          (r) => (r as { buildingId?: string }).buildingId === s.buildingId
        );
        for (const room of buildingRooms) {
          const bookingId = `${s.eventId}-${room.spaceId}`;
          bookings.push({
            id: bookingId,
            eventTitle: displayTitle,
            roomId: room.spaceId,
            start: new Date(s.eventStartDate),
            end: new Date(s.eventEndDate),
          });
          idMap.set(bookingId, s.eventId);
        }
      }
    }
  }

  return { bookings, bookingToEventId: idMap };
}
